import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, Button, IconButton } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LogoutIcon from '@mui/icons-material/Logout';
import logo from '../logo.png';
import GreenBackground from './ui/Green_Background.jpg';

/**
 * Reusable Banner for all admin pages
 * @param {string} title - The page title (e.g., 'Sales History', 'Settlement', etc.)
 * @param {function} handleBack - Callback for back button (optional)
 * @param {function} handleLogout - Callback for logout button (optional)
 * @param {React.ReactNode} rightContent - Optional extra content on the right (e.g., extra buttons)
 */
const Banner = ({ title, handleBack, handleLogout, rightContent }) => (
  <Box
    sx={{
      backgroundImage: `linear-gradient(rgba(0, 0, 0, .8), rgba(0, 0, 0, 0.6)), url(${GreenBackground})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      color: '#fff',
      padding: { xs: '8px 4px', sm: '10px 24px' },
      position: 'sticky',
      top: 0,
      left: 0,
      right: 0,
      width: '100%',
      maxWidth: '100vw',
      overflowX: 'auto',
      boxSizing: 'border-box',
      zIndex: 1201,
      margin: 0,
      borderRadius: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      minHeight: { xs: 56, sm: 70 },
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
      {handleBack && (
        <Button
          variant="text"
          onClick={handleBack}
          sx={{
            color: '#fff',
            minWidth: 0,
            padding: { xs: '4px', sm: '8px' },
            mr: 2,
            backgroundColor: 'transparent',
            '&:hover': { backgroundColor: 'rgba(255,255,255,0.08)' },
          }}
        >
          <ArrowBackIcon sx={{ fontSize: { xs: 24, sm: 32 } }} />
        </Button>
      )}
      <Box
        sx={{
          width: { xs: 40, sm: 60 },
          height: { xs: 40, sm: 60 },
          borderRadius: '50%',
          overflow: 'hidden',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          mr: 2,
          flexShrink: 0,
        }}
      >
        <img src={logo} alt="Brewz Coffee Logo" style={{ width: '100%', height: 'auto' }} />
      </Box>
      <Typography
        variant={(title && title.length > 18) ? 'h5' : 'h4'}
        sx={{
          color: '#fff',
          fontWeight: 700,
          fontSize: { xs: '1.1rem', sm: '1.5rem' },
          textOverflow: 'ellipsis',
          overflow: 'hidden',
          whiteSpace: 'nowrap',
        }}
        noWrap
      >
        {title || ''}
      </Typography>
    </Box>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      {rightContent}
      {handleLogout && (
        <IconButton
          onClick={handleLogout}
          sx={{
            color: '#FB4141',
            '&:hover': {
              backgroundColor: 'rgba(251, 65, 65, 0.1)',
            },
          }}
          aria-label="logout"
        >
          <LogoutIcon />
        </IconButton>
      )}
    </Box>
  </Box>
);

Banner.propTypes = {
  title: PropTypes.string,
  handleBack: PropTypes.func,
  handleLogout: PropTypes.func,
  rightContent: PropTypes.node,
};

Banner.defaultProps = {
  title: '',
};

// For future: can extend with more props (e.g. user, extra buttons, etc)
export default Banner;
